import React, { createContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserMessage } from '../../DesignSystem/LiliUserMessage/UserMessage';
import { PlanPickerState, UserMessageContentSwitch } from './comopnents/UserMessageContentSwitch';
import { ILiliPlan } from './types';
import { useStore } from '../../store';
import { useUpgradePlan } from '../../hooks/mutations/useUpgradeToProMutation';
import { getAvailablePlans, getInitialPlan } from './service';
import { extractErrorFromCode } from '../../helpers';
import { validateFeatureFlag } from '../../services/featureFlagService';
import { FEATURE_FLAG } from '../../constants/general';
import { useGetPlansData } from './hooks/useGetPlansData';
import Spinner, { SpinnerColor } from '../../generic-components/Spinner';
import { GenericErrorModal } from '../../components/errors/GenericErrorModal';
import { ResetSlicesForAccountSwitching } from '../../store/service';
import { VIEWS_MAP } from '../../router/constants';
import { useAccountSummaryQuery } from '../../hooks/query/useAccountSummaryQuery';

interface IProps {
    onCloseClick: () => void
    initialPlanType?: string
    origin: string
    promotionType?: string
}
interface IReportState{
    didReportPickerView: boolean
    didReportConfirmationView: boolean
    didReportSuccessView: boolean
}
interface IFlowContext {
    isLoading: boolean
    reportsState: IReportState
    mutateReportState?: (data: IReportState) => void
}

export const FlowContext = createContext<IFlowContext>({
  isLoading: false,
  reportsState: {
    didReportPickerView: false,
    didReportConfirmationView: false,
    didReportSuccessView: false,
  },

});
export const PlanUpgradeFlow = ({
  onCloseClick, initialPlanType, origin, promotionType,
}: IProps) => {
  const summaryData = useStore(state => state.summary);
  const [reportsState, setReportsState] = useState<IReportState>({
    didReportPickerView: false,
    didReportConfirmationView: false,
    didReportSuccessView: false,
  });
  const [errorToShow, setErrorToShow] = useState<string | undefined>(undefined);
  const { data: planPickerData, isLoading: isFetchingPlansData, refetch: reloadPlans } = useGetPlansData({
    onError: (errorMessage) => {
      setErrorToShow(errorMessage);
    },
  });
  const { refetch: refetchSummary, isRefetching: isFetchingSummary, isLoading: isLoadingSummary } = useAccountSummaryQuery();
  const { mutateAsync, isLoading: isUpgrading } = useUpgradePlan();
  const flowStateContext: IFlowContext = useMemo(() => ({
    isLoading: isUpgrading || isFetchingSummary || isLoadingSummary,
    mutateReportState: (data: IReportState) => setReportsState(data),
    reportsState,
  }), [isUpgrading, isFetchingSummary, isLoadingSummary]);
  const { customer: { customerProfile, externalId } } = summaryData;
  const [activeComponent, setActiveComponent] = useState<PlanPickerState>(PlanPickerState.Picker);
  const [selectedPlan, setSelectedPlan] = useState<ILiliPlan>(getInitialPlan(customerProfile, planPickerData?.plans || [], initialPlanType));
  const navigate = useNavigate();
  const shouldTriggerTOS = validateFeatureFlag(FEATURE_FLAG.PLAN_UPGRADE_TERMS_AND_CONDITIONS);
  const isAnnualRef = useRef<boolean>(false);

  const updatePlan = (plan?: ILiliPlan, isAnnual?: boolean) => {
    const activePlan = plan ?? selectedPlan;
    const isAnnualPlan = isAnnual || isAnnualRef.current;
    mutateAsync({ customerId: externalId, plan: activePlan.type, isAnnual: isAnnualPlan })
      .catch((errorCode) => {
        const error = extractErrorFromCode(errorCode);
        setErrorToShow(error);
        setActiveComponent(PlanPickerState.Error);
      }).then(() => {
        setActiveComponent(PlanPickerState.Success);
      });
  };
  const handleOnPlanSubmitted = (plan: ILiliPlan, isAnnual: boolean) => {
    setSelectedPlan(plan);
    isAnnualRef.current = isAnnual;
    if (shouldTriggerTOS) {
      setActiveComponent(PlanPickerState.Confirmation);
    } else {
      updatePlan(plan, isAnnual);
    }
  };

  const onGreatClick = () => {
    ResetSlicesForAccountSwitching();
    navigate(`/${VIEWS_MAP.DASHBOARD}`);

    refetchSummary().then(() => {
      onCloseClick();
    });
  };

  const renderContent = () => {
    if (isFetchingPlansData) {
      return <Spinner spinnerColor={SpinnerColor.White} height='3rem' width='3rem' />;
    } if (planPickerData) {
      return (
        <FlowContext.Provider value={flowStateContext}>
          <UserMessageContentSwitch
            activeComponent={activeComponent}
            onCloseClick={onCloseClick}
            onUpgradeClick={handleOnPlanSubmitted}
            onGreatClick={onGreatClick}
            onTermsConfirmed={updatePlan}
            onErrorActionClicked={() => {
              setErrorToShow(undefined);
              setActiveComponent(PlanPickerState.Picker);
            }}
            selectedPlan={selectedPlan}
            currentPlan={customerProfile}
            availablePlans={getAvailablePlans(customerProfile, planPickerData)}
            origin={origin}
            promotionType={promotionType}
            errorToShow={errorToShow}
          />
        </FlowContext.Provider>
      );
    } if (errorToShow) {
      return (
        <GenericErrorModal
          title='Oops'
          description={errorToShow}
          buttonTitle='Retry'
          onActionClick={reloadPlans}
        />
      );
    }
    return '';
  };

  return (
    <UserMessage
      id='PlanUpgradeFlow'
      onClick={() => {
        if (activeComponent === PlanPickerState.Success) {
          onGreatClick();
        } else {
          onCloseClick();
        }
      }}
      contentWrapperOverrideClassName='w-fit max-w-fit max-h-fit h-fit  flex items-center justify-center'
    >
      {renderContent()}

    </UserMessage>
  );
};
